/**
 * posDocCounterRange — per-device PS / SR document number range allocation.
 *
 * Implements Decision 1 from IDEATION_OFFLINE_PWA.md: pre-allocated ranges.
 *
 * When online, this device fetches a range of numbers (e.g. PS 101-200) from
 * the central Firestore counter via runTransaction. The range is stored in
 * localStorage. While offline, the next sale takes the next number from this
 * range. When 80% consumed (and online), a fresh range is auto-fetched.
 *
 * Unused numbers when the range is "topped up" early are STRANDED (visible
 * gaps in invoice numbering) — accepted trade-off per the locked-in decision.
 *
 * Scope: PS and SR only (POS offline MVP). Other tran types continue using
 * the original generateDocNumber in transactionHelper.js.
 *
 * Layout in localStorage (one key per [tranType, branch, year]):
 *   key:  amal.docRange.PS.<branchId|none>.<year>
 *   val:  { prefix, start, end, cursor, fetchedAt, devicePrefix }
 *
 * Where:
 *   start..end inclusive, cursor is next-unused (>= start, <= end+1)
 *   prefix from settings/documentNumbering (or tranType default)
 *   devicePrefix: 1-char tag for traceability (NOT part of the doc number —
 *     this is only stored locally so we can tell which device generated which
 *     number range if there's ever a collision investigation)
 */

import {
  collection, doc, getDoc, runTransaction
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getTranType } from '../config/tranTypes';

const RANGE_SIZE = 100;     // numbers per fetch
const REFETCH_AT = 0.8;     // top up when 80% consumed (range_size * 0.8)
const TRAN_CODE_TO_DOC_KEY = { PS: 'posCounterSale', SR: 'saleReturn' };

// ─── Device identity (random per browser install) ──────────────────────

const DEVICE_ID_KEY = 'amal.deviceId';

const getDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'd' + Math.random().toString(36).slice(2, 12);
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return 'device-fallback';
  }
};

export const getDeviceCode = () => getDeviceId().slice(0, 8);

// ─── localStorage range storage ────────────────────────────────────────

// Per-counter continuous numbering: each counter has its own pre-allocated
// range (key includes counterId). Falls back to a single global series ('cont')
// when no counter is supplied.
const rangeKey = (companyId, tranTypeCode, counterId) =>
  `amal.docRange.${companyId}.${tranTypeCode}.${counterId || 'cont'}`;

const readRange = (companyId, tranTypeCode, counterId) => {
  try {
    const raw = localStorage.getItem(rangeKey(companyId, tranTypeCode, counterId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeRange = (companyId, tranTypeCode, counterId, value) => {
  try {
    localStorage.setItem(rangeKey(companyId, tranTypeCode, counterId), JSON.stringify(value));
  } catch { /* quota — ignore */ }
};

// ─── Doc-numbering settings lookup ─────────────────────────────────────

const loadNumberingSettings = async (companyId, tranTypeCode) => {
  const tranType = getTranType(tranTypeCode);
  let prefix = tranType?.prefix || '';
  let padding = 5;
  let startNumber = 1;
  try {
    const docKey = TRAN_CODE_TO_DOC_KEY[tranTypeCode];
    if (docKey) {
      const settingsRef = doc(db, 'companies', companyId, 'settings', 'documentNumbering');
      const snap = await getDoc(settingsRef);
      if (snap.exists()) {
        const entry = snap.data()?.[docKey];
        if (entry) {
          if (entry.prefix && String(entry.prefix).trim()) {
            const raw = String(entry.prefix).trim();
            prefix = raw.endsWith('-') ? raw : `${raw}-`;
          }
          if (Number(entry.padding)) padding = Math.min(Math.max(Number(entry.padding), 3), 8);
          if (Number(entry.startNumber)) startNumber = Number(entry.startNumber);
        }
      }
    }
  } catch (e) { /* fall back to defaults */ }
  return { prefix, padding, startNumber };
};

// ─── Allocate a fresh range from the cloud counter ─────────────────────

/**
 * Atomically claim the next RANGE_SIZE numbers from the central counter for
 * (tranTypeCode, branchId, year). Requires online.
 *
 * Returns { start, end, prefix, padding } where start..end is the inclusive
 * range of sequence numbers reserved for this device.
 */
export const allocateRange = async (companyId, tranTypeCode, counter = null) => {
  const { prefix, padding, startNumber } = await loadNumberingSettings(companyId, tranTypeCode);
  // Per-counter continuous counter (shares the doc with the online generator).
  const counterKey = counter?.id ? `${tranTypeCode}__${counter.id}` : `${tranTypeCode}__cont`;
  const counterCode = counter?.counterCode || null;
  const counterRef = doc(db, 'companies', companyId, 'documentCounters', counterKey);

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    let current = 0;
    if (snap.exists()) {
      current = Number(snap.data().current) || 0;
    }
    const start = Math.max(current, startNumber - 1) + 1;
    const end = start + RANGE_SIZE - 1;
    transaction.set(counterRef, {
      prefix,
      current: end, // reserve up to `end`
      updatedAt: new Date().toISOString(),
      lastRangeAllocatedTo: getDeviceId(),
      lastRangeAllocatedAt: new Date().toISOString()
    });
    return { start, end, prefix, padding, counterCode };
  });
};

// ─── Public API ────────────────────────────────────────────────────────

/**
 * Return the next document number for (tranTypeCode, branchId, docDate).
 * If we have a non-exhausted local range, take from it (works offline).
 * If we don't, and we're online, allocate a fresh range and take from it.
 * If we don't, and we're offline, throw.
 *
 * If the range is past the REFETCH_AT threshold AND we're online, the next
 * range is fetched in the background (non-blocking — current call still
 * returns immediately from the existing range).
 */
export const getNextDocNumber = async ({
  companyId, tranTypeCode, counter = null, isOnline
}) => {
  const counterId = counter?.id || null;
  let range = readRange(companyId, tranTypeCode, counterId);

  // No range yet, or current range exhausted
  const exhausted = !range || range.cursor > range.end;
  if (exhausted) {
    if (!isOnline) {
      throw new Error(
        `Out of pre-allocated ${tranTypeCode} numbers. ` +
        `Connect to the internet briefly to fetch the next range.`
      );
    }
    const fresh = await allocateRange(companyId, tranTypeCode, counter);
    range = {
      prefix: fresh.prefix,
      padding: fresh.padding,
      counterCode: fresh.counterCode || null,
      start: fresh.start,
      end: fresh.end,
      cursor: fresh.start,
      fetchedAt: new Date().toISOString(),
      devicePrefix: getDeviceCode()
    };
  }

  const seq = range.cursor;
  range.cursor = seq + 1;
  writeRange(companyId, tranTypeCode, counterId, range);

  // Schedule background top-up if we've crossed the threshold
  const used = (seq - range.start + 1);
  const capacity = (range.end - range.start + 1);
  if (isOnline && used / capacity >= REFETCH_AT) {
    // Fire-and-forget — failure is harmless, we'll retry on next use
    scheduleBackgroundTopUp(companyId, tranTypeCode, counter);
  }

  const seg = range.counterCode ? `${range.counterCode}-` : '';
  const padded = String(seq).padStart(range.padding || 5, '0');
  return `${range.prefix}${seg}${padded}`;
};

// ─── Background top-up ─────────────────────────────────────────────────

let _topupInFlight = {};

const scheduleBackgroundTopUp = (companyId, tranTypeCode, counter = null) => {
  const counterId = counter?.id || null;
  const key = `${companyId}.${tranTypeCode}.${counterId || 'cont'}`;
  if (_topupInFlight[key]) return;
  _topupInFlight[key] = true;

  setTimeout(async () => {
    try {
      const fresh = await allocateRange(companyId, tranTypeCode, counter);
      // Overwrite the local range with the freshly reserved one — losing the
      // current range's tail is the accepted trade-off (numbers may skip, never
      // repeat).
      writeRange(companyId, tranTypeCode, counterId, {
        prefix: fresh.prefix,
        padding: fresh.padding,
        counterCode: fresh.counterCode || null,
        start: fresh.start,
        end: fresh.end,
        cursor: fresh.start,
        fetchedAt: new Date().toISOString(),
        devicePrefix: getDeviceCode()
      });
    } catch (e) {
      console.warn('[posDocCounterRange] background top-up failed:', e);
    } finally {
      delete _topupInFlight[key];
    }
  }, 0);
};

// ─── Proactive pre-allocation (call while online) ──────────────────────

/**
 * Ensure a usable range exists for (tranTypeCode, branchId, year). If one
 * already exists with remaining numbers, no-op. Otherwise allocate a fresh
 * range from the cloud counter so the device is ready to go offline.
 *
 * MUST be called while online — silently no-ops if offline (the existing
 * range, if any, will still work; without one the next offline save will
 * throw the actionable error message).
 *
 * Call this:
 *   - When POSScreen mounts (if online)
 *   - On reconnect (after being offline for a while)
 */
export const ensureRangeAllocated = async ({
  companyId, tranTypeCode, counter = null, isOnline
}) => {
  if (!isOnline) return { ok: false, reason: 'offline' };
  const counterId = counter?.id || null;
  const existing = readRange(companyId, tranTypeCode, counterId);
  if (existing && existing.cursor <= existing.end) {
    return { ok: true, reason: 'already-has-range', remaining: existing.end - existing.cursor + 1 };
  }
  try {
    const fresh = await allocateRange(companyId, tranTypeCode, counter);
    writeRange(companyId, tranTypeCode, counterId, {
      prefix: fresh.prefix,
      padding: fresh.padding,
      counterCode: fresh.counterCode || null,
      start: fresh.start,
      end: fresh.end,
      cursor: fresh.start,
      fetchedAt: new Date().toISOString(),
      devicePrefix: getDeviceCode()
    });
    return { ok: true, reason: 'fresh-range', start: fresh.start, end: fresh.end };
  } catch (err) {
    console.warn('[posDocCounterRange] ensureRangeAllocated failed:', err);
    return { ok: false, reason: 'cloud-error', error: err };
  }
};

// ─── Diagnostics ────────────────────────────────────────────────────────

